import { IAuthProvider, LoginCredentials, AuthResult, MfaVerificationCredentials, PasswordResetRequest, PasswordResetSubmit } from './IAuthProvider';
import { User, Role } from '@/types/domain/user';
import { ROLE_PERMISSIONS_MAP, DEFAULT_ROLE_SCOPES } from '@/config/permissions';
import { auditService } from '@/services/audit/AuditService';

export const DEMO_USERS: Record<Role, User> = {
  EMPLOYEE: {
    id: 'USR-101',
    employeeId: 'EMP-9001',
    name: 'Sarah Jenkins',
    email: 'employee@procure.com',
    role: 'EMPLOYEE',
    roles: ['EMPLOYEE'],
    departmentCode: 'DEP-IT',
    departmentName: 'Information Technology',
    plantCode: 'PLANT-1000',
    businessUnit: 'BU-TECH',
    companyCode: '1000',
    approvalAuthorityLimit: 5000,
    status: 'ACTIVE',
    accessScope: DEFAULT_ROLE_SCOPES.EMPLOYEE,
    permissions: ROLE_PERMISSIONS_MAP.EMPLOYEE,
  },
  MANAGER: {
    id: 'USR-102',
    employeeId: 'EMP-9002',
    name: 'Marcus Vance',
    email: 'manager@procure.com',
    role: 'MANAGER',
    roles: ['MANAGER'],
    departmentCode: 'DEP-IT',
    departmentName: 'Information Technology',
    plantCode: 'PLANT-1000',
    businessUnit: 'BU-TECH',
    companyCode: '1000',
    approvalAuthorityLimit: 50000,
    status: 'ACTIVE',
    accessScope: DEFAULT_ROLE_SCOPES.MANAGER,
    permissions: ROLE_PERMISSIONS_MAP.MANAGER,
  },
  PROCUREMENT: {
    id: 'USR-103',
    employeeId: 'EMP-9003',
    name: 'Elena Rostova',
    email: 'procurement@procure.com',
    role: 'PROCUREMENT',
    roles: ['PROCUREMENT'],
    departmentCode: 'DEP-PROC',
    departmentName: 'Global Sourcing & Procurement',
    plantCode: 'PLANT-1000',
    businessUnit: 'BU-GLOBAL',
    companyCode: '1000',
    approvalAuthorityLimit: 250000,
    status: 'ACTIVE',
    accessScope: DEFAULT_ROLE_SCOPES.PROCUREMENT,
    permissions: ROLE_PERMISSIONS_MAP.PROCUREMENT,
  },
  PROCUREMENT_MANAGER: {
    id: 'USR-104',
    employeeId: 'EMP-9004',
    name: 'David Sterling',
    email: 'procmanager@procure.com',
    role: 'PROCUREMENT_MANAGER',
    roles: ['PROCUREMENT_MANAGER'],
    departmentCode: 'DEP-PROC',
    departmentName: 'Global Sourcing & Procurement',
    plantCode: 'PLANT-1000',
    businessUnit: 'BU-GLOBAL',
    companyCode: '1000',
    approvalAuthorityLimit: 1000000,
    status: 'ACTIVE',
    accessScope: DEFAULT_ROLE_SCOPES.PROCUREMENT_MANAGER,
    permissions: ROLE_PERMISSIONS_MAP.PROCUREMENT_MANAGER,
  },
  FINANCE: {
    id: 'USR-105',
    employeeId: 'EMP-9005',
    name: 'Rachel Chen',
    email: 'finance@procure.com',
    role: 'FINANCE',
    roles: ['FINANCE'],
    departmentCode: 'DEP-FIN',
    departmentName: 'Enterprise Finance & Treasury',
    plantCode: 'PLANT-1000',
    businessUnit: 'BU-GLOBAL',
    companyCode: '1000',
    approvalAuthorityLimit: 500000,
    status: 'ACTIVE',
    accessScope: DEFAULT_ROLE_SCOPES.FINANCE,
    permissions: ROLE_PERMISSIONS_MAP.FINANCE,
  },
  NFA_LFA_APPROVER: {
    id: 'USR-106',
    employeeId: 'EMP-9006',
    name: 'Arthur Pendelton',
    email: 'approver@procure.com',
    role: 'NFA_LFA_APPROVER',
    roles: ['NFA_LFA_APPROVER'],
    departmentCode: 'DEP-EXEC',
    departmentName: 'Executive Steering Committee',
    plantCode: 'PLANT-1000',
    businessUnit: 'BU-GLOBAL',
    companyCode: '1000',
    approvalAuthorityLimit: 5000000,
    status: 'ACTIVE',
    accessScope: DEFAULT_ROLE_SCOPES.NFA_LFA_APPROVER,
    permissions: ROLE_PERMISSIONS_MAP.NFA_LFA_APPROVER,
  },
  MANAGEMENT: {
    id: 'USR-107',
    employeeId: 'EMP-9007',
    name: 'Victoria Thorne',
    email: 'management@procure.com',
    role: 'MANAGEMENT',
    roles: ['MANAGEMENT'],
    departmentCode: 'DEP-EXEC',
    departmentName: 'Executive Steering Committee',
    plantCode: 'PLANT-1000',
    businessUnit: 'BU-GLOBAL',
    companyCode: '1000',
    approvalAuthorityLimit: 10000000,
    status: 'ACTIVE',
    accessScope: DEFAULT_ROLE_SCOPES.MANAGEMENT,
    permissions: ROLE_PERMISSIONS_MAP.MANAGEMENT,
  },
  SAP_INTEGRATION: {
    id: 'USR-108',
    employeeId: 'SYS-SAP-01',
    name: 'SAP Integration Gateway',
    email: 'sap@procure.com',
    role: 'SAP_INTEGRATION',
    roles: ['SAP_INTEGRATION'],
    departmentCode: 'DEP-IT',
    departmentName: 'Enterprise Integration',
    plantCode: 'PLANT-1000',
    businessUnit: 'BU-GLOBAL',
    companyCode: '1000',
    approvalAuthorityLimit: 0,
    status: 'ACTIVE',
    accessScope: DEFAULT_ROLE_SCOPES.SAP_INTEGRATION,
    permissions: ROLE_PERMISSIONS_MAP.SAP_INTEGRATION,
  },
  ADMIN: {
    id: 'USR-109',
    employeeId: 'EMP-9009',
    name: 'Alexander Hamilton',
    email: 'admin@procure.com',
    role: 'ADMIN',
    roles: ['ADMIN'],
    departmentCode: 'DEP-IT',
    departmentName: 'Information Technology',
    plantCode: 'PLANT-1000',
    businessUnit: 'BU-GLOBAL',
    companyCode: '1000',
    approvalAuthorityLimit: 99999999,
    status: 'ACTIVE',
    accessScope: DEFAULT_ROLE_SCOPES.ADMIN,
    permissions: ROLE_PERMISSIONS_MAP.ADMIN,
  },
  AUDITOR: {
    id: 'USR-110',
    employeeId: 'EMP-9010',
    name: 'Grace Hopper',
    email: 'auditor@procure.com',
    role: 'AUDITOR',
    roles: ['AUDITOR'],
    departmentCode: 'DEP-AUDIT',
    departmentName: 'Compliance & Internal Audit',
    plantCode: 'PLANT-1000',
    businessUnit: 'BU-GLOBAL',
    companyCode: '1000',
    approvalAuthorityLimit: 0,
    status: 'ACTIVE',
    accessScope: DEFAULT_ROLE_SCOPES.AUDITOR,
    permissions: ROLE_PERMISSIONS_MAP.AUDITOR,
  },
};

export class MockAuthProvider implements IAuthProvider {
  private currentUser: User | null = DEMO_USERS.ADMIN;
  private failedAttempts = 0;
  private lockoutUntil: number | null = null;

  async login(credentials: LoginCredentials): Promise<AuthResult> {
    // Check account lockout
    if (this.lockoutUntil && Date.now() < this.lockoutUntil) {
      const remainingSeconds = Math.ceil((this.lockoutUntil - Date.now()) / 1000);
      auditService.log('ACCOUNT_LOCKED', undefined, { remainingSeconds });
      return {
        success: false,
        errorCode: 'ACCOUNT_LOCKED',
        errorMessage: `Account temporarily locked due to repeated failed attempts. Please retry in ${remainingSeconds} seconds.`,
        lockoutRemainingSeconds: remainingSeconds,
      };
    }

    // Generic error response to prevent account enumeration
    if (!credentials.emailOrEmployeeId || (credentials.password && credentials.password.length < 6)) {
      this.failedAttempts += 1;
      if (this.failedAttempts >= 5) {
        this.lockoutUntil = Date.now() + 60 * 1000; // 60s lockout
        auditService.log('ACCOUNT_LOCKED', undefined, { failedAttempts: this.failedAttempts });
      } else {
        auditService.log('LOGIN_FAILED', undefined, { failedAttempts: this.failedAttempts });
      }

      return {
        success: false,
        errorCode: 'INVALID_CREDENTIALS',
        errorMessage: 'Invalid credentials or sign-in information. Please verify your details.',
      };
    }

    // Flexible match by exact email, employee ID, or role alias
    const input = credentials.emailOrEmployeeId.trim().toLowerCase();
    let matchedUser = Object.values(DEMO_USERS).find(
      (u) =>
        u.email.toLowerCase() === input ||
        u.employeeId.toLowerCase() === input
    );

    if (!matchedUser) {
      if (input.includes('employe') || input.includes('sarah')) {
        matchedUser = DEMO_USERS.EMPLOYEE;
      } else if (input.includes('procmanager') || input.includes('procurement_manager') || input.includes('david')) {
        matchedUser = DEMO_USERS.PROCUREMENT_MANAGER;
      } else if (input.includes('procurement') || input.includes('buyer') || input.includes('elena')) {
        matchedUser = DEMO_USERS.PROCUREMENT;
      } else if (input.includes('manager') || input.includes('marcus')) {
        matchedUser = DEMO_USERS.MANAGER;
      } else if (input.includes('finance') || input.includes('accounts') || input.includes('rachel')) {
        matchedUser = DEMO_USERS.FINANCE;
      } else if (input.includes('approver') || input.includes('nfa') || input.includes('arthur')) {
        matchedUser = DEMO_USERS.NFA_LFA_APPROVER;
      } else if (input.includes('management') || input.includes('cpo') || input.includes('victoria')) {
        matchedUser = DEMO_USERS.MANAGEMENT;
      } else if (input.includes('sap')) {
        matchedUser = DEMO_USERS.SAP_INTEGRATION;
      } else if (input.includes('auditor') || input.includes('audit') || input.includes('grace')) {
        matchedUser = DEMO_USERS.AUDITOR;
      } else if (input.includes('admin') || input.includes('alexander')) {
        matchedUser = DEMO_USERS.ADMIN;
      } else {
        matchedUser = DEMO_USERS.ADMIN;
      }
    }

    this.failedAttempts = 0;
    this.lockoutUntil = null;
    this.currentUser = matchedUser;

    auditService.log('LOGIN_SUCCESS', { id: matchedUser.id, email: matchedUser.email, role: matchedUser.role });

    return {
      success: true,
      user: matchedUser,
    };
  }

  async verifyMfa(credentials: MfaVerificationCredentials): Promise<AuthResult> {
    if (credentials.otpCode === '123456') {
      auditService.log('MFA_SUCCESS', { id: credentials.userId });
      return { success: true, user: this.currentUser || DEMO_USERS.ADMIN };
    }
    auditService.log('MFA_FAILURE', { id: credentials.userId });
    return {
      success: false,
      errorCode: 'MFA_FAILED',
      errorMessage: 'Invalid 6-digit authentication code.',
    };
  }

  async logout(): Promise<void> {
    if (this.currentUser) {
      auditService.log('LOGOUT', { id: this.currentUser.id, email: this.currentUser.email, role: this.currentUser.role });
    }
    this.currentUser = null;
  }

  async getCurrentUser(): Promise<User | null> {
    return this.currentUser;
  }

  async requestPasswordReset(payload: PasswordResetRequest): Promise<{ success: boolean; message: string }> {
    auditService.log('PASSWORD_RESET_REQUESTED', undefined, { email: payload.email });
    // Generic non-enumerating confirmation response
    return {
      success: true,
      message: 'If an account matches that email address, password recovery instructions have been sent.',
    };
  }

  async resetPassword(payload: PasswordResetSubmit): Promise<{ success: boolean; message: string }> {
    if (payload.token === 'invalid') {
      return { success: false, message: 'Password reset link is invalid or has expired.' };
    }
    auditService.log('PASSWORD_RESET_COMPLETED');
    return { success: true, message: 'Password has been successfully updated.' };
  }

  async switchRole(role: Role): Promise<User> {
    const targetUser = DEMO_USERS[role];
    if (targetUser) {
      this.currentUser = targetUser;
      auditService.log('ROLE_CHANGED', { id: targetUser.id, email: targetUser.email, role: targetUser.role });
      return targetUser;
    }
    return this.currentUser || DEMO_USERS.ADMIN;
  }
}

export const mockAuthProvider = new MockAuthProvider();
